import { makeStyles, Theme } from '@material-ui/core';
import { BaseEntityField, BaseEntityFieldArray } from '@moderepo/mode-app-components';
import { EntityMetricsDefinition, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import { AggregatedDataIntervalType, MetricMetadata, MetricsMetadata, RawDataIntervalType, TSDBDataSourceType } from '../../containers';
import { TypeNone } from '../../utils/utils';
import { EntityInputField } from '../entities';
import { MetricsMetadataInputsCompProps } from './MetricsMetadataInputsComp';


// 'value' comes from TEXT input fields therefore it will be a string. For metadataData props that are type Number, we need
// to cast the value to Number. This will be the set of metricMetadata props that are type Number
export const numericMetadataProps: readonly (keyof MetricMetadata) [] = [
    'yAxisMin', 'yAxisMax', 'lowValueWarningThreshold', 'lowValueCriticalThreshold', 'highValueWarningThreshold',
    'highValueCriticalThreshold', 'decimalPlaces',
];



/**
 * Shared style used by all dashboard widget components
 */
export const dashboardWidgetCompStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        formInputSectionTitle: {
            '&.error': {
                color    : theme.palette.error.main,
                '& .icon': {
                    color: theme.palette.error.main,
                },
            },
        },

        metadataInputSection: {
            fontSize: 'smaller',

            '&.error': {
                color    : theme.palette.error.main,
                '& .icon': {
                    color: theme.palette.error.main,
                },
            },

            '& [class*="MuiInputLabel-shrink"]': {
                fontSize: 'small',
            },

            '& [class*="ModeForm-formInputSectionTitle"]': {
                borderBottomWidth: 1,
                fontWeight       : 400,
            },

            '& [class*="ModeForm-formInputSectionContent"]': {
                marginTop   : 15,
                marginBottom: 15,
            },

            '& [class*="ModeForm-formInputField"]': {
                marginBottom: 15,
            },

            '& [class*="ModeForm-formInputGroup"]': {
                rowGap   : 0,
                columnGap: 10,
            },

            '& .MuiOutlinedInput-input': {
                fontSize: 'smaller',
                padding : '10px 10px 7px 10px',
            },
        },
    };
}, { name: 'DashboardWidgetCompStyle', index: 1 });


/**
 * Interface for the input fields required for BASE dashboard widget
 */
export interface BaseDashboardWidgetSettingsCompFields {
    readonly title: BaseEntityField<string>;
    readonly icon: BaseEntityField<string>;
}


/**
 * Interface for the input fields required for base dashboard widget that contain data
 */
export interface DataDashboardWidgetSettingsCompFields {
    readonly reloadDataFreq: BaseEntityField<number>;
}


/**
 * Interface for the input fields required for dashboard widget that has TSDB as data source
 */
export interface TSDBDashboardWidgetSettingsFields {
    readonly dataSource: {
        readonly homeId: BaseEntityField<number>;
        readonly dataSourceType: BaseEntityField<TSDBDataSourceType>;

        // These are OPTIONAL fields only needed for TSDB data source type
        readonly smartModuleId?: BaseEntityField<string> | undefined;
        readonly seriesIds?: BaseEntityFieldArray<string> | undefined;
        readonly collectionId?: BaseEntityField<string> | undefined;

        // These are OPTIONAL fields only needed for ENTITY data source type
        readonly entity?: EntityInputField | undefined;
        readonly metricsDefinition?: BaseEntityField<EntityMetricsDefinition> | undefined;

        readonly valueNames: BaseEntityFieldArray<string>;
        readonly tagNames?: BaseEntityFieldArray<string> | undefined;
        readonly aggregationType: BaseEntityField<TimeSeriesAggregation | TypeNone>;
        readonly intervalType: BaseEntityField<AggregatedDataIntervalType | RawDataIntervalType>;

        readonly metricsMetadata: BaseEntityField<MetricsMetadata>;
    }
}


/**
 * Interface for the texts settings that are required for dashboard widget that use TSDB data
 */
export interface TSDBDashboardWidgetSettingsCompTexts {
    readonly dataSourceSettings: string;
    readonly viewSettings: string;
    readonly metricsMetadataLabels: MetricsMetadataInputsCompProps['texts'];
}


/**
 * Convert metrics metadata input values from strings to numbers for the metric props that are type numbers
 *
 * Because HTML Input element only work with text therefore the input values are string. We need to convert numeric
 * metricsMetadataInput values to numbers. This block of code will convert this
 *
 * From:
 *      {
 *          temperature: {name: "Temperature", unit:"C", min:"5", max:"10"},
 *          pressure: {min:"100", max: "200"}
 *      }
 *
 * To:
 *      {
 *          temperature: {name: "Temperature", unit:"C", min:5, max:10},
 *          pressure: {min:100, max: 200}
 *      }
 *
 *
 * @param metricsMetadataInput
 * @returns
 */
export const castMetricsMetadataInputs = (metricsMetadataInput: MetricsMetadata | undefined): MetricsMetadata | undefined => {

    if (metricsMetadataInput) {
        return Object.keys(metricsMetadataInput).reduce((multipleMetricsMetadata: MetricsMetadata, metricName): MetricsMetadata => {
            const singleMetricMetadata = metricsMetadataInput[metricName];
            if (singleMetricMetadata) {
                return {
                    ...multipleMetricsMetadata,
                    [metricName]: {
                        // copy all the metadata from metricMetadata first, this includes the STRING and NUMERIC metadata
                        ...singleMetricMetadata,

                        // For each numericMetadataProps, convert the value for that metadata prop (if the value exist) to number and return
                        // the numeric value to replace the string value
                        ...numericMetadataProps.reduce((numericMetadata, metadataName) => {
                            const metadataValue = singleMetricMetadata[metadataName];
                            if (metadataValue !== undefined) {
                                if (typeof metadataValue === 'string' && metadataValue.trim() === '') {
                                    return {
                                        ...numericMetadata,
                                        [metadataName]: undefined,
                                    };
                                }
                                return {
                                    ...numericMetadata,
                                    [metadataName]: Number(metadataValue),
                                };

                            }
                            return numericMetadata;
                        }, {}),
                    },
                };
            }
            return multipleMetricsMetadata;
        }, {});
    }

    return undefined;
};
